import React, { useState } from 'react';
import { Header } from '@/components/layout/Header';
import { Badge } from '@/components/ui/badge';
import { ConcentrationOverview } from '@/components/brand-dependency/ConcentrationOverview';
import { ReplaceabilityScore } from '@/components/brand-dependency/ReplaceabilityScore';
import { RiskSimulation } from '@/components/brand-dependency/RiskSimulation';
import { StrategicRecommendation } from '@/components/brand-dependency/StrategicRecommendation';

// Page Header Component
const PageHeader = () => (
  <div className="mb-10" data-testid="page-header">
    <div className="flex items-center gap-3 mb-2">
      <Badge className="bg-primary/10 text-primary border-primary/20">CONFIDENTIAL</Badge>
      <Badge className="bg-destructive/20 text-destructive border-destructive/30">Risk Analysis</Badge>
    </div>
    <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
      Brand Concentration & Dependency Analysis
    </h1>
    <p className="text-muted-foreground mt-2">
      Team-level brand exposure and risk concentration modelling
    </p>
  </div>
);

// Assessment Info Component
const AssessmentInfo = () => (
  <div className="mb-8 p-4 rounded-lg bg-muted/30 border border-border/50">
    <div className="flex flex-wrap items-center gap-x-8 gap-y-2 text-sm">
      <div>
        <span className="text-muted-foreground">Assessment Date: </span>
        <span className="font-medium text-foreground">
          {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </span>
      </div>
      <div>
        <span className="text-muted-foreground">Subject: </span>
        <span className="font-medium text-foreground">USMNT Roster</span>
      </div>
      <div>
        <span className="text-muted-foreground">Classification: </span>
        <span className="font-medium text-foreground">Board-Level Risk Assessment</span>
      </div>
    </div>
  </div>
);

// Footer Disclaimer Component
const FooterDisclaimer = () => (
  <div className="mt-10 p-6 rounded-lg bg-muted/20 border border-border/50">
    <h3 className="text-sm font-semibold text-foreground mb-3">Important Disclaimer</h3>
    <p className="text-xs text-muted-foreground leading-relaxed">
      The brand concentration metrics, replaceability scores, and risk simulations presented in this analysis 
      are directional indicators derived from proprietary analytical models and publicly available data signals. 
      All projections represent strategic planning estimates, not certified valuations. Player availability scenarios 
      are hypothetical and intended for risk assessment purposes only. This document is intended solely for internal 
      strategic planning and board-level decision-making within the U.S. Soccer Federation. Distribution beyond 
      authorized recipients requires written consent.
    </p>
    <div className="mt-4 pt-4 border-t border-border/50 flex items-center justify-between">
      <div className="text-xs text-muted-foreground">
        <span className="font-semibold text-primary">YBV</span> Brand Intelligence Platform
      </div>
      <div className="text-xs text-muted-foreground">
        ISO 10668 Aligned | BSI-Aligned Methodology | Interbrand-Aligned Valuation
      </div>
    </div>
  </div>
);

// Main Brand Dependency Page
export default function BrandDependency() {
  const [selectedScenario, setSelectedScenario] = useState('no_injury');

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      
      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <PageHeader />
          <AssessmentInfo />
          <ConcentrationOverview />
          <ReplaceabilityScore />
          <RiskSimulation 
            selectedScenario={selectedScenario} 
            onScenarioChange={setSelectedScenario} 
          />
          <StrategicRecommendation />
          <FooterDisclaimer />
        </div>
      </main>
    </div>
  );
}
